import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { WBAirplaneFill } from '@/domain/models'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import {
  IWBAirplaneFillRepository,
  RequestGetWBAirplaneFillsResponse,
  WBAirplaneFillDTO
} from './IWBAirplaneFillRepository'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'
import type { IHttpClient } from '@/infra/http/IHttpClient'

@injectable()
export class WBAirplaneFillRepository implements IWBAirplaneFillRepository {
  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState
  ) {}

  protected getRemoteUrl(): string {
    return `${NEXATLAS_API_BASE}/wbProfileAirplaneFill`
  }

  async getAll(): Promise<Result<WBAirplaneFill[]>> {
    try {
      const response = await this.httpClient.get<RequestGetWBAirplaneFillsResponse[]>(`${this.getRemoteUrl()}`, {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        }
      })

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      return Result.ok(response.response.map((dto) => this._remoteToLocal(dto)).filter((fill) => !fill.deletedAt))
    } catch (error: any) {
      console.error('Error in getAllWBAirplaneFills:', error)
      return Result.fail(`Failed to get all WB airplane fills: ${error}`)
    }
  }

  async getById(id: UniqueEntityID): Promise<Result<WBAirplaneFill | null>> {
    try {
      const response = await this.httpClient.get<RequestGetWBAirplaneFillsResponse>(
        `${this.getRemoteUrl()}/${id.toString()}`,
        {
          headers: {
            Authorization: `Token ${this.authState.getStateSnapshot().token}`
          }
        }
      )

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      return Result.ok(response.response.deletedAt ? null : this._remoteToLocal(response.response))
    } catch (error) {
      console.error('Error in getWBAirplaneFillById:', error)
      return Result.fail(`Failed to get WB airplane fill by ID: ${error}`)
    }
  }

  async getByAircraftId(aircraftId: UniqueEntityID): Promise<Result<WBAirplaneFill[]>> {
    try {
      const response = await this.httpClient.get<RequestGetWBAirplaneFillsResponse[]>(
        `${this.getRemoteUrl()}/userAircraft/${aircraftId.toString()}`,
        {
          headers: {
            Authorization: `Token ${this.authState.getStateSnapshot().token}`
          }
        }
      )

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      const fills = response.response.map((dto) => this._remoteToLocal(dto)).filter((fill) => !fill.deletedAt)
      return Result.ok(fills)
    } catch (error) {
      console.error('Error in getWBAirplaneFillsByAircraft:', error)
      return Result.fail(`Error fetching WB airplane fills for aircraft: ${error}`)
    }
  }

  async getByProfileId(profileId: UniqueEntityID): Promise<Result<WBAirplaneFill[]>> {
    try {
      const response = await this.httpClient.get<RequestGetWBAirplaneFillsResponse[]>(
        `${this.getRemoteUrl()}/wbProfileAirplane/${profileId.toString()}`,
        {
          headers: {
            Authorization: `Token ${this.authState.getStateSnapshot().token}`
          }
        }
      )

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      const fills = response.response.map((dto) => this._remoteToLocal(dto)).filter((fill) => !fill.deletedAt)
      return Result.ok(fills)
    } catch (error) {
      console.error('Error in getWBAirplaneFillsByProfile:', error)
      return Result.fail(`Error fetching WB airplane fills for profile: ${error}`)
    }
  }

  async create(filling: WBAirplaneFill): Promise<Result<void>> {
    try {
      const dto = this._localToRemote(filling)

      const response = await this.httpClient.post(`${this.getRemoteUrl()}`, dto, {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        }
      })

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      return Result.ok()
    } catch (error) {
      console.error('Error in saveWBAirplaneFill:', error)
      return Result.fail(`Failed to save WB airplane fill: ${error}`)
    }
  }

  async upsert(fill: WBAirplaneFill): Promise<Result<void>> {
    try {
      const createResult = await this.create(fill)
      if (createResult.isFailure) return Result.fail(createResult.error)
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to update WB airplane fill: ${error}`)
    }
  }

  async delete(id: UniqueEntityID): Promise<Result<void>> {
    try {
      const response = await this.httpClient.delete(
        `${this.getRemoteUrl()}/${id.toString()}`,
        {},
        {
          headers: {
            Authorization: `Token ${this.authState.getStateSnapshot().token}`
          }
        }
      )
      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      return Result.ok()
    } catch (error) {
      console.error('Error in deleteWBAirplaneFill:', error)
      return Result.fail(`Failed to delete WB airplane fill: ${error}`)
    }
  }

  // Private methods

  private _remoteToLocal(dto: RequestGetWBAirplaneFillsResponse): WBAirplaneFill {
    return WBAirplaneFill.create(
      {
        aircraftId: new UniqueEntityID(dto.userAircraftId),
        wbProfileAirplaneId: new UniqueEntityID(dto.wbProfileAirplaneId),
        name: dto.name,
        departure: dto.departure,
        arrival: dto.arrival,
        departureTime: dto.departureTime ? new Date(dto.departureTime) : undefined,
        pilotName: dto.pilotName,
        pilotCode: dto.pilotCode,
        remark: dto.remark,
        taxiFuel: dto.taxiFuel,
        flightFuel: dto.flightFuel,
        stationsFill: dto.stationsFill?.map((station) => ({
          id: new UniqueEntityID(station.id),
          wbProfileAirplaneStationId: new UniqueEntityID(station.wbProfileAirplaneStationId),
          weight: station.weight
        })),
        createdAt: new Date(dto.createdAt),
        updatedAt: new Date(dto.updatedAt),
        deletedAt: dto.deletedAt ? new Date(dto.deletedAt) : null
      },
      dto.id ? new UniqueEntityID(dto.id) : undefined
    )
  }

  private _localToRemote(filling: WBAirplaneFill): WBAirplaneFillDTO {
    return {
      id: filling.id ? filling.id.toString() : new UniqueEntityID().toString(),
      userAircraftId: filling.aircraftId.toString(),
      wbProfileAirplaneId: filling.wbProfileAirplaneId.toString(),
      name: filling.name,
      departure: filling.departure,
      arrival: filling.arrival,
      departureTime: filling.departureTime ? filling.departureTime.toISOString() : undefined,
      pilotName: filling.pilotName,
      pilotCode: filling.pilotCode,
      remark: filling.remark,
      taxiFuel: filling.taxiFuel,
      flightFuel: filling.flightFuel,
      stationsFill: filling.stationsFill?.map((station) => ({
        id: station.id.toString(),
        wbProfileAirplaneStationId: station.wbProfileAirplaneStationId.toString(),
        weight: station.weight
      }))
    } as WBAirplaneFillDTO
  }
}
